import {
  ArrowRight, Check, Disc3, Headphones, Heart, Link2, LoaderCircle,
  Music2, Radio, Sparkles, UserRound
} from 'lucide-react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { post, ApiError, api } from '../api';
import { useAuth } from '../App';
import Modal from '../components/Modal';
import PageMotion from '../components/PageMotion';
import { money, number } from '../format';
import { PUBLIC_OFFER_URL, PUBLIC_OFFER_VERSION } from '../legal';
import type { ArtistCatalog, ArtistRelease, ArtistWithReleases, LinkPreview, Order, Plan, User } from '../types';

const categories = [
  { id: 'plays', label: 'Прослушивания', icon: Headphones },
  { id: 'listeners', label: 'Слушатели', icon: Radio },
  { id: 'album_likes', label: 'Лайки альбома', icon: Heart },
  { id: 'artist_likes', label: 'Лайки артиста', icon: UserRound }
];

export default function ServicesPage() {
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [catalog, setCatalog] = useState<ArtistCatalog>({ artists: [] });
  const [selectedArtistId, setSelectedArtistId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Plan | null>(null);
  const [targetMode, setTargetMode] = useState<'catalog' | 'link'>('link');
  const [targetUrl, setTargetUrl] = useState('');
  const [chosenRelease, setChosenRelease] = useState<ArtistRelease | null>(null);
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [pending, setPending] = useState(false);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [success, setSuccess] = useState<Order | null>(null);
  const [error, setError] = useState('');
  const category = params.get('category') || 'plays';

  useEffect(() => {
    api<{ plans: Plan[] }>('/plans').then((value) => setPlans(value.plans));
    api<ArtistCatalog>('/profile/artists').then((value) => {
      setCatalog(value);
      if (value.artists.length === 1) setSelectedArtistId(value.artists[0].id);
    }).catch(() => {});
  }, []);

  const selectedArtist = useMemo(() => (
    catalog.artists.find((artist) => artist.id === selectedArtistId) || null
  ), [catalog.artists, selectedArtistId]);

  useEffect(() => {
    if (!selected) return;
    const useCatalog = catalog.artists.length > 0;
    const nextArtist = catalog.artists.length === 1 ? catalog.artists[0] : null;
    setTargetMode(useCatalog ? 'catalog' : 'link');
    setSelectedArtistId(nextArtist?.id || null);
    setChosenRelease(null);
    setPreview(null);
    setPreviewError('');
    setError('');
    setOfferAccepted(false);
    setTargetUrl(selected.category === 'artist_likes' && nextArtist ? nextArtist.artist_url : '');
  }, [selected, catalog.artists]);

  useEffect(() => {
    if (!selected || targetMode !== 'link') return;
    setPreview(null);
    setPreviewError('');
    const value = targetUrl.trim();
    if (!/^https?:\/\//i.test(value)) { setPreviewLoading(false); return; }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      api<{ info: LinkPreview }>('/catalog/preview', {
        method: 'POST',
        body: JSON.stringify({ plan_id: selected.id, target_url: value }),
        signal: controller.signal
      }).then((result) => setPreview(result.info))
        .catch((reason) => {
          if (!controller.signal.aborted) setPreviewError(reason instanceof Error ? reason.message : 'Не удалось получить информацию');
        })
        .finally(() => { if (!controller.signal.aborted) setPreviewLoading(false); });
    }, 650);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [selected, targetMode, targetUrl]);

  const visible = useMemo(() => plans.filter((plan) => plan.category === category), [plans, category]);
  const isArtistOrder = selected?.category === 'artist_likes';
  const targetReady = targetMode === 'catalog' ? Boolean(targetUrl) : Boolean(preview && !previewLoading && !previewError);

  const selectRelease = (release: ArtistRelease) => {
    setChosenRelease(release);
    setTargetUrl(release.album_url);
    setError('');
  };

  const selectArtist = (artist: ArtistWithReleases) => {
    setSelectedArtistId(artist.id);
    setChosenRelease(null);
    setPreview(null);
    setPreviewError('');
    setError('');
    setTargetUrl(isArtistOrder ? artist.artist_url : '');
  };

  const switchMode = (mode: 'catalog' | 'link') => {
    setTargetMode(mode); setChosenRelease(null); setPreview(null); setPreviewError(''); setError('');
    const artist = selectedArtist || (catalog.artists.length === 1 ? catalog.artists[0] : null);
    if (mode === 'catalog' && artist && selectedArtistId === null) setSelectedArtistId(artist.id);
    setTargetUrl(mode === 'catalog' && isArtistOrder && artist ? artist.artist_url : '');
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !targetReady) return;
    setPending(true); setError('');
    try {
      const result = await post<{ order: Order; user: User }>('/orders', {
        plan_id: selected.id,
        target_url: targetUrl,
        offer_accepted: offerAccepted,
        offer_version: PUBLIC_OFFER_VERSION
      });
      setUser(result.user); setSuccess(result.order);
    } catch (reason) {
      const message = reason instanceof ApiError && reason.status === 409 ? 'На балансе недостаточно средств для этого тарифа.' : reason instanceof Error ? reason.message : 'Не удалось создать заказ';
      setError(message);
      api<{ user: User }>('/auth/me').then((value) => setUser(value.user)).catch(() => {});
    } finally { setPending(false); }
  };

  const close = () => {
    setSelected(null); setSuccess(null); setError(''); setTargetUrl(''); setChosenRelease(null);
    setPreview(null); setPreviewError(''); setPreviewLoading(false); setOfferAccepted(false);
  };

  return (
    <PageMotion>
      <header className="page-header"><div><span className="eyebrow">Каталог</span><h1>Услуги</h1><p>Выберите подходящий объём продвижения.</p></div></header>
      <div className="category-switcher">
        {categories.map(({ id, label, icon: Icon }) => <button key={id} className={category === id ? 'active' : ''} onClick={() => setParams({ category: id })}><Icon size={17} />{label}</button>)}
      </div>
      <section className="plans-grid">
        {!plans.length ? Array.from({ length: 4 }, (_, index) => <div className="plan-card skeleton" key={index} />) : visible.map((plan, index) => (
          <button className={`plan-card tone-border-${plan.accent}`} key={plan.id} onClick={() => setSelected(plan)}>
            <span className="plan-index">{String(index + 1).padStart(2, '0')}</span>
            <div><span className="plan-kind">{categories.find((item) => item.id === category)?.label}</span><h2>{number(plan.quantity)}</h2><p>{plan.unit}</p></div>
            <div className="plan-foot"><div><strong>{money(plan.price)}</strong><small>{(plan.price / plan.quantity).toFixed(2)} ₽ за единицу</small></div><span><ArrowRight size={18} /></span></div>
          </button>
        ))}
      </section>

      <Modal open={Boolean(selected)} title={success ? 'Заказ создан' : 'Новый заказ'} eyebrow={success ? 'Готово' : 'Оформление'} onClose={close}>
        {success ? <div className="success-state"><span><Check size={28} /></span><h3>{success.title}</h3><p>Заказ #{success.id} добавлен в работу. Прогресс появится в разделе «Заказы».</p><button className="primary-button full" onClick={close}>Продолжить</button></div> : selected && <form className="order-form" onSubmit={create}>
          <div className="selected-plan"><span className={`service-icon tone-${selected.accent}`}><Sparkles size={19} /></span><div><b>{selected.title}</b><small>{selected.description}</small></div><strong>{money(selected.price)}</strong></div>

          {catalog.artists.length > 0 && <div className="target-mode-switch" role="tablist" aria-label="Способ выбора">
            <button type="button" className={targetMode === 'catalog' ? 'active' : ''} onClick={() => switchMode('catalog')}><Music2 size={16} /> Мой артист</button>
            <button type="button" className={targetMode === 'link' ? 'active' : ''} onClick={() => switchMode('link')}><Link2 size={16} /> Вставить ссылку</button>
          </div>}

          {targetMode === 'catalog' && catalog.artists.length > 0 ? <div className="order-catalog-flow">
            <section className="order-artist-step">
              <div className="order-step-heading"><span>1</span><div><b>Выберите артиста</b><small>{catalog.artists.length === 1 ? 'Привязан один профиль' : `${catalog.artists.length} профилей в вашей библиотеке`}</small></div></div>
              <div className="order-artist-grid">
                {catalog.artists.map((artist) => {
                  const active = selectedArtist?.id === artist.id;
                  return <button type="button" className={`order-artist-option ${active ? 'selected' : ''}`} key={artist.id} onClick={() => selectArtist(artist)}>
                    {artist.cover_url ? <img src={artist.cover_url} alt="" /> : <span><UserRound size={20} /></span>}
                    <div><strong title={artist.name}>{artist.name}</strong><small>{artist.releases.length} релизов</small></div>
                    <i>{active && <Check size={13} />}</i>
                  </button>;
                })}
              </div>
            </section>

            {selectedArtist && (isArtistOrder ? <button className="saved-artist-choice selected" type="button">
              {selectedArtist.cover_url ? <img src={selectedArtist.cover_url} alt="" /> : <span><UserRound size={22} /></span>}
              <div><small>Карточка артиста выбрана</small><strong>{selectedArtist.name}</strong><p>{number(selectedArtist.listeners)} слушателей в месяц</p></div><Check size={18} />
            </button> : <div className="release-picker-block">
              <div className="release-picker-head"><span>2</span><div><b>Выберите релиз</b><small>{selectedArtist.name} · {selectedArtist.releases.length} релизов</small></div></div>
              {selectedArtist.releases.length ? <div className="release-picker">
                {selectedArtist.releases.map((release) => <button type="button" className={`release-option ${chosenRelease?.id === release.id ? 'selected' : ''}`} key={release.id} onClick={() => selectRelease(release)}>
                  <div>{release.cover_url ? <img src={release.cover_url} alt="" loading="lazy" /> : <Disc3 size={23} />}{chosenRelease?.id === release.id && <i><Check size={13} /></i>}</div>
                  <strong title={release.title}>{release.title}</strong><small>{release.year || 'Релиз'}</small>
                </button>)}
              </div> : <div className="catalog-empty"><Disc3 size={22} /><span>У артиста пока нет доступных релизов.</span></div>}
            </div>)}
          </div> : <div className="manual-target">
            {!catalog.artists.length && <div className="profile-connect-hint"><Music2 size={18} /><div><b>Добавьте артиста в профиль</b><span>Тогда сначала можно будет выбрать артиста, а затем релиз.</span></div><Link to="/profile">Добавить</Link></div>}
            <label>Ссылка на трек, альбом или артиста<input value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} type="url" placeholder="https://music.yandex.ru/..." required autoFocus /></label>
            {previewLoading && <div className="link-preview-loading"><LoaderCircle size={18} className="spinning" /><div><b>Получаю информацию...</b><span>Проверяю ссылку в Яндекс.Музыке</span></div></div>}
            {previewError && <div className="form-error">{previewError}</div>}
            {preview && <article className="link-preview-card">
              {preview.cover_url ? <img src={preview.cover_url} alt="" /> : <span><Music2 size={23} /></span>}
              <div><small>{preview.type === 'artist' ? 'Артист' : preview.type === 'track' ? 'Трек' : preview.type === 'album' ? 'Релиз' : 'Яндекс.Музыка'}</small><strong>{preview.title}</strong><p>{preview.subtitle || 'Ссылка проверена'}</p></div><Check size={18} />
            </article>}
          </div>}

          <div className={`balance-check ${(user?.balance || 0) < selected.price ? 'danger' : ''}`}><span>Ваш баланс</span><strong>{money(user?.balance || 0)}</strong></div>
          <label className="legal-consent">
            <input type="checkbox" checked={offerAccepted} onChange={(event) => setOfferAccepted(event.target.checked)} />
            <span>Я принимаю условия <a href={PUBLIC_OFFER_URL} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Публичной оферты</a> (PDF, редакция {PUBLIC_OFFER_VERSION})</span>
          </label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary-button full" disabled={pending || !targetReady || !offerAccepted || (user?.balance || 0) < selected.price}>{pending ? <span className="spinner" /> : <>Создать заказ · {money(selected.price)}</>}</button>
          {targetMode === 'catalog' && !selectedArtist && <p className="form-hint">Сначала выберите артиста</p>}
          {targetMode === 'catalog' && selectedArtist && !targetReady && <p className="form-hint">Выберите релиз для оформления заказа</p>}
          {(user?.balance || 0) < selected.price && <p className="form-hint">Пополните баланс на {money(selected.price - (user?.balance || 0))}</p>}
        </form>}
      </Modal>
    </PageMotion>
  );
}
